import { useRef, useState } from 'react'

import cloneDeep from 'lodash/cloneDeep'

import type { Filter, FilterValueProductMetafield, FilterValues } from '@lib/types'

type Props = {
	filter: Filter
	filters: FilterValues
	setFilters: (newFilters: FilterValues) => void
}

const STARS_ARRAY = [5, 4, 3, 2, 1]

export default function StarsFilter({ filter, filters, setFilters }: Props) {
	const [open, toggle] = useState(false)

	const inputsRef = useRef<HTMLInputElement[]>([])

	/**
	 * use the first filter value as a base for the new filters,
	 * replacing only the input value
	 */
	const baseFilterValue = filter.values[0] as FilterValueProductMetafield

	/**
	 * use the base filter and replace its value with the new one
	 */
	const getNewFilterObject = (star: string) => {
		const newFilter = cloneDeep(baseFilterValue)
		newFilter.input.productMetafield.value = star

		return newFilter
	}

	/**
	 * Get all decimals of a number and use it as a filter
	 */
	const getStarDecimals = (star: number) => {
		const starDecimals = []

		/**
		 * generate filters with all decimals of the star number
		 * e.g. [3.0, 3.1, ..., 3.9]
		 */
		for (let i = 0; i < 10; i++) {
			const decimalValue = (star + i / 10).toFixed(1)

			const input = getNewFilterObject(decimalValue)

			starDecimals.push(input)
		}

		return starDecimals
	}

	const onChangeHandler = (checked: boolean, star: number) => {
		// remove all stars filters. The filters will be rebuild if a new star is checked
		const newFilters: FilterValues = filters.filter(
			(existingFilter) =>
				'productMetafield' in existingFilter.input && // TypeScript type inference
				existingFilter.input.productMetafield.key !== 'avg_rating'
		)

		// add a filter
		if (checked) {
			const starsFilters: FilterValues = []

			// if filter is 5, filter only 5.0
			if (star === 5) {
				starsFilters.push(getNewFilterObject('5.0'))
			} else {
				// filter stars from the selected one up to 5 stars
				for (let i = star; i <= 5; i++) {
					if (i < 5) {
						const starDecimals = getStarDecimals(i)

						starsFilters.push(...starDecimals)
					} else {
						// filter only 5.0
						starsFilters.push(getNewFilterObject('5.0'))
					}
				}
			}

			newFilters.push(...starsFilters)
		}
		// remove a filter
		else {
			// uncheck all stars inputs
			for (let i = 1; i <= 5; i++) {
				inputsRef.current[i].checked = false
			}
		}

		setFilters(newFilters)
	}

	return (
		<div className="py-3">
			<button
				type="button"
				className="flex justify-between w-full py-1 font-medium"
				onClick={() => toggle((prev) => !prev)}
			>
				Stars
				<span className={`transition duration-300 ease-in-out ${open ? 'rotate-180' : 'rotate-0'}`}>
					<i className="fa-regular fa-angle-down text-gray-500" />
				</span>
			</button>

			<div className={`pt-3 pb-4 space-y-4 ${open ? '' : 'hidden'}`}>
				{STARS_ARRAY.map((star: number) => (
					<div className="flex gap-2.5" key={`stars-filter-${star}`}>
						<input
							type="radio"
							id={`star-${star}`}
							name="stars-filter"
							onChange={(e) => onChangeHandler(e.target.checked, star)}
							ref={(el) => {
								inputsRef.current[star] = el as HTMLInputElement
							}}
							className="w-3.5 h-3.5 mt-0.5 border-gray-350 border-2 text-bbx-spark rounded-sm focus:ring-bbx-spark/40 focus:ring-2 focus:ring-offset-0 focus:border-0"
						/>
						<label htmlFor={`star-${star}`} className="font-light leading-5">
							{star} {star > 1 ? 'Stars' : 'Star'} {star < 5 && '& up'}
						</label>
					</div>
				))}
			</div>
		</div>
	)
}
